import type { DashboardKpi } from "@swot/swot-components";

import type {
  CmtsDashboardDrilldownVm,
  CmtsDashboardVm,
} from "../view-models/dashboardViewModels.js";

import {
  readArray,
  readBoolean,
  readDateIso,
  readNumber,
  readString,
} from "./mapperUtils.js";

export function mapDashboard(
  dto: unknown,
  dashboardType: string,
): CmtsDashboardVm {
  const cards = readArray(
    dto,
    ["cards", "kpis", "items"],
    mapKpiCard,
  );

  return {
    dashboardType: readString(
      dto,
      ["dashboardType", "dashboardCode", "code"],
      dashboardType,
    ),

    tenantId: readString(
      dto,
      ["tenantId"],
      "",
    ),

    title: readString(
      dto,
      ["title", "dashboardName", "name"],
      `${toDisplayName(dashboardType)} Dashboard`,
    ),

    subtitle: readString(
      dto,
      ["subtitle", "description"],
      "Backend-scoped dashboard metrics and drilldowns.",
    ),

    generatedAtUtc: readDateIso(
      dto,
      ["generatedAtUtc", "generatedAt"],
      "",
    ),

    generatedAtLocal: readString(
      dto,
      ["generatedAtLocal"],
      "",
    ),

    timezoneCode: readString(
      dto,
      ["timezoneCode", "tenantTimezone"],
      "Asia/Kolkata",
    ),

    cards,

    diagnostics:
      cards.length === 0
        ? [
            {
              code: "CMTS_DASHBOARD_EMPTY",
              message:
                "No KPI cards were returned for this dashboard.",
              severity: "warning",
            },
          ]
        : [],

    stale: readBoolean(
      dto,
      ["stale", "fallbackUsed"],
      false,
    ),

    restricted: readBoolean(
      dto,
      ["restricted", "visibilityRestricted"],
      false,
    ),
  };
}

export function mapKpiCard(
  dto: unknown,
): DashboardKpi {
  const source =
    typeof dto === "object" &&
    dto !== null &&
    !Array.isArray(dto)
      ? (dto as Record<string, unknown>)
      : {};

  const kpiCode = readString(
    dto,
    ["kpiCode", "id", "cardId", "code"],
    "UNKNOWN_KPI",
  );

  const valueText = readString(
    dto,
    ["value"],
    "",
  );

  const numeric = readNumber(
    dto,
    ["value"],
    Number.NaN,
  );

  const visible = readBoolean(
    dto,
    ["visible"],
    true,
  );

  const label = readString(
    dto,
    [
      "displayName",
      "label",
      "title",
      "kpiLabel",
      "name",
    ],
    toDisplayName(kpiCode),
  );

  const description = readString(
    dto,
    [
      "description",
      "message",
      "noRecordsMessage",
    ],
    "",
  );

  return {
    id: kpiCode,

    label,

    value: visible
      ? Number.isFinite(numeric)
        ? numeric
        : valueText || "Not available"
      : "Restricted",

    trend: normalizeTrend(
      readString(dto, ["trend"], "flat"),
    ),

    drilldownPath: readString(
      dto,
      [
        "drilldownPath",
        "drilldownRoute",
        "targetRoute",
      ],
      "",
    ),

    tone: visible
      ? "info"
      : "warning",

    ...(source.targetRoute
      ? { targetRoute: source.targetRoute }
      : {}),

    ...(source.filterPayload
      ? { filterPayload: source.filterPayload }
      : {}),

    ...(source.expectedGrid
      ? { expectedGrid: source.expectedGrid }
      : {}),

    ...(source.countSelectorId
      ? { countSelectorId: source.countSelectorId }
      : {}),

    ...(source.gridSelectorId
      ? { gridSelectorId: source.gridSelectorId }
      : {}),

    ...(source.scopeMode
      ? { scopeMode: source.scopeMode }
      : {}),

    ...(source.valueState
      ? { valueState: source.valueState }
      : {}),

    ...(description
      ? { description }
      : {}),
  } as DashboardKpi;
}

export function mapDashboardDrilldown(
  dto: unknown,
): CmtsDashboardDrilldownVm {
  return {
    kpiCode: readString(
      dto,
      ["kpiCode"],
      "UNKNOWN_KPI",
    ),

    rows: readArray(
      dto,
      ["rows"],
      (value) => ({
        ...(typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : {}),
      }),
    ),

    totalCount: readNumber(
      dto,
      ["totalCount", "totalRecords"],
      0,
    ),

    exportAllowed: readBoolean(
      dto,
      ["exportAllowed"],
      false,
    ),

    visibilityRestricted: readBoolean(
      dto,
      ["visibilityRestricted"],
      false,
    ),

    diagnostics: [],
  };
}

function normalizeTrend(
  value: string,
): DashboardKpi["trend"] {
  const normalized =
    value.toLowerCase();

  return normalized === "up" ||
    normalized === "down"
    ? normalized
    : "flat";
}

function toDisplayName(
  value: string,
): string {
  const normalized = value
    .trim()
    .replace(/[-_]+/g, " ")
    .toLowerCase();

  if (!normalized) {
    return "Unnamed KPI";
  }

  return normalized.replace(
    /\b\w/g,
    (character) =>
      character.toUpperCase(),
  );
}